/**
 * Form signature field value shape.
 * PNG is stored as a base64 data URL (same as waiver pre_waiver_signature_url).
 * Metadata is kept for disputes.
 */
export type FormSignatureValue = {
  dataUrl: string;
  signedAt: string;
  signedBy?: string | null;
  ipAddress?: string | null;
  userAgent?: string | null;
};

const DATA_URL_RE = /^data:image\/(png|jpeg|jpg|webp);base64,/i;

export function isSignatureDataUrl(value: unknown): value is string {
  return typeof value === 'string' && DATA_URL_RE.test(value) && value.length > 40;
}

export function isFormSignatureValue(value: unknown): value is FormSignatureValue {
  if (!value || typeof value !== 'object') return false;
  const v = value as FormSignatureValue;
  return isSignatureDataUrl(v.dataUrl);
}

/** Extract displayable PNG data URL from stored field value (object or bare string). */
export function getSignatureDataUrl(value: unknown): string | null {
  if (isFormSignatureValue(value)) return value.dataUrl;
  if (isSignatureDataUrl(value)) return value;
  return null;
}

export function getSignatureSignedAt(value: unknown): string | null {
  if (isFormSignatureValue(value) && value.signedAt) return value.signedAt;
  return null;
}

export function buildFormSignatureValue(params: {
  dataUrl: string;
  signedBy?: string | null;
}): FormSignatureValue {
  return {
    dataUrl: params.dataUrl,
    signedAt: new Date().toISOString(),
    signedBy: params.signedBy ?? null,
  };
}

/** Required signature field is empty if no stroke/data URL present. */
export function isSignatureFieldEmpty(value: unknown): boolean {
  return getSignatureDataUrl(value) == null;
}

/**
 * Best-effort signer name from other form answers (name / full name fields).
 */
export function inferSignedBy(fieldValues: Record<string, unknown>): string | null {
  for (const [key, val] of Object.entries(fieldValues)) {
    if (typeof val !== 'string' || !val.trim()) continue;
    const k = key.toLowerCase();
    if (k.includes('name') || k.includes('signed') || k.includes('customer')) {
      return val.trim().slice(0, 120);
    }
  }
  for (const val of Object.values(fieldValues)) {
    if (typeof val !== 'string') continue;
    // skip data urls
    if (val.startsWith('data:')) continue;
  }
  return null;
}
